import { useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

type NavItem = { label: string; to: string };
type NavGroup = { title: string; items: NavItem[] };

const navGroups: NavGroup[] = [
  { title: 'General', items: [{ label: 'Dashboard', to: '/' }] },
  {
    title: 'System Administration',
    items: [
      { label: 'Tenants', to: '/tenants' },
      { label: 'System Users', to: '/system-users' },
      { label: 'Settings', to: '/settings' },
      { label: 'ITIL Configuration', to: '/itil-configuration' },
      { label: 'Teams Integration', to: '/integrations/teams' },
      { label: 'System Info', to: '/system-info' },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      { label: 'System Logs', to: '/monitoring/logs' },
      { label: 'Scheduled Jobs', to: '/monitoring/jobs' },
      { label: 'AI Assistant Usage', to: '/monitoring/assistant-usage' },
      { label: 'Service Bus', to: '/monitoring/service-bus' },
      { label: 'Events', to: '/monitoring/events' },
      { label: 'Performance', to: '/monitoring/performance' },
      { label: 'Auto-Close', to: '/monitoring/auto-close' },
      { label: 'Email History', to: '/monitoring/email-history' },
      { label: 'Sign-In Logs', to: '/monitoring/signin-logs' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { label: 'Feedback & Bugs', to: '/tools/feedback-bugs' },
      { label: 'Table Browser', to: '/tools/table-browser' },
    ],
  },
  { title: 'Documentation', items: [{ label: 'Documentation', to: '/documentation' }] },
];

export function AdminLayout() {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const appVersion = useMemo(() => import.meta.env.VITE_APP_VERSION || '0.0.0', []);
  const userEmail = user?.email ?? 'Unknown user';
  const userInitial = userEmail.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <button
            className="rounded border border-gray-300 px-2 py-1 text-sm md:hidden"
            onClick={() => setIsOpen((value) => !value)}
            type="button"
          >
            Menu
          </button>
          <div className="h-8 w-8 rounded bg-primary-700" />
          <h1 className="font-semibold">CloudIncidents Admin</h1>
        </div>
        <div className="relative">
          <button
            type="button"
            className="flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-left shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            onClick={() => setIsUserMenuOpen((value) => !value)}
            aria-expanded={isUserMenuOpen}
            aria-haspopup="menu"
            aria-label="Open user menu"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white">
              {userInitial}
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium leading-tight">{userEmail}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">SystemAdmin</p>
            </div>
            <svg
              className={`h-4 w-4 text-gray-500 transition-transform dark:text-gray-400 ${isUserMenuOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isUserMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={() => setIsUserMenuOpen(false)}
                aria-hidden="true"
              />
              <div
                className="absolute right-0 z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                role="menu"
                aria-orientation="vertical"
              >
                <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userEmail}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SystemAdmin</p>
                </div>

                <div className="py-1">
                  <NavLink
                    to="/profile"
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    role="menuitem"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Profile
                  </NavLink>
                  <NavLink
                    to="/monitoring/signin-logs"
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    role="menuitem"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Sign-In Logs
                  </NavLink>
                </div>

                <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                  <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Theme</p>
                  <div className="flex rounded-md bg-gray-100 p-1 dark:bg-gray-700">
                    {(['system', 'light', 'dark'] as const).map((nextTheme) => (
                      <button
                        key={nextTheme}
                        type="button"
                        className={`flex-1 rounded px-2 py-1 text-xs capitalize ${
                          theme === nextTheme
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                            : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                        onClick={() => setTheme(nextTheme)}
                      >
                        {nextTheme}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 p-2 dark:border-gray-700">
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      logout();
                    }}
                    role="menuitem"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex min-w-0 overflow-x-hidden">
        <aside
          className={`fixed z-10 h-[calc(100vh-3.5rem)] w-72 border-r border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 md:static md:block ${
            isOpen ? 'block' : 'hidden'
          }`}
        >
          {navGroups.map((group) => (
            <div key={group.title} className="mb-5">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.title}</h2>
              <nav className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `block rounded px-3 py-2 text-sm ${isActive ? 'bg-primary-700 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </aside>
        <main className="min-h-[calc(100vh-3.5rem)] min-w-0 max-w-full flex-1 overflow-x-hidden p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-gray-200 bg-white px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        Version {appVersion}
      </footer>
    </div>
  );
}
